import AdminSettingsPanel from "./AdminSettingsPanel";

function formatHours(value) {
  return Number.isFinite(value) ? `${value}h` : "–";
}

function sortedEntries(record) {
  return Object.entries(record || {}).sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0));
}

function typeLabel(value) {
  if (value === "PhotoUpdate") return "Photo update";
  if (value === "PhotoRemoval") return "Photo removal";
  return value || "Unknown";
}

function Breakdown({ title, entries, label = (value) => value, limit = 8 }) {
  const visible = entries.slice(0, limit);
  const max = Math.max(1, ...visible.map(([, count]) => Number(count || 0)));

  return <section className="operations-breakdown">
    <h4>{title}</h4>
    {!visible.length && <small>No data in the current metrics sample.</small>}
    {visible.map(([name, count]) => {
      const width = Math.max(4, Math.round((Number(count || 0) / max) * 100));
      return <div className="operations-breakdown-row" key={name}>
        <div><span>{label(name)}</span><strong>{count}</strong></div>
        <div className="operations-breakdown-track" aria-hidden="true"><span style={{ width: `${width}%` }} /></div>
      </div>;
    })}
  </section>;
}

function WorkflowHealth({ health }) {
  if (!health) return null;
  const title = health.overall === "critical" ? "Critical attention needed"
    : health.overall === "attention" ? "Attention needed" : "Healthy";
  return <section className={`workflow-health ${health.overall || "healthy"}`}>
    <div className="workflow-health-heading">
      <div><small>Workflow health</small><strong>{title}</strong></div>
      <span>{health.issues?.length || 0} active issue{(health.issues?.length || 0) === 1 ? "" : "s"}</span>
    </div>
    {health.issues?.length
      ? <div className="workflow-health-issues">{health.issues.map((issue) => <div key={issue.code} className={`workflow-health-issue ${issue.severity}`}><span>{issue.label}</span><strong>{issue.count}</strong></div>)}</div>
      : <small className="workflow-health-clear">No stuck processing, Graph failures, routing gaps, or failed notifications were detected in the current sample.</small>}
    {!health.queueDepthAvailable && <small className="workflow-health-limit">Application-state monitoring only. Azure queue depth and alert rules are handled separately in Azure monitoring.</small>}
  </section>;
}

export default function OperationsMetrics({ metrics, generatedAt, getAccessToken }) {
  if (!metrics) return <div className="operations-metrics-empty">Workflow metrics are not available from this API version yet.</div>;

  const completionRate = Number.isFinite(metrics.completionRatePercent) ? `${metrics.completionRatePercent}%` : "–";
  const cards = [
    ["Total requests", metrics.total ?? 0],
    ["Pending", metrics.pending ?? 0],
    ["Completed", metrics.completed ?? 0],
    ["Completion rate", completionRate],
    ["Rejected", metrics.rejected ?? 0],
    ["Failed / support", metrics.failed ?? 0],
    ["Expired", metrics.expired ?? 0],
    ["Cancelled", metrics.cancelled ?? 0],
  ];

  return <>
    <div className="operations-metrics">
      <div className="operations-metrics-heading">
        <div><h4>Workflow metrics</h4><small>Read-only lifecycle indicators from the bounded operations sample.</small></div>
        {generatedAt && <small>Updated {new Date(generatedAt).toLocaleString()}</small>}
      </div>

      <WorkflowHealth health={metrics.health} />

      <div className="operations-metric-grid">
        {cards.map(([label, value]) => <div className="operations-metric-card" key={label}><small>{label}</small><strong>{value}</strong></div>)}
      </div>

      <div className="operations-time-grid">
        <div><small>Average HC decision</small><strong>{formatHours(metrics.averageHcHours)}</strong></div>
        <div><small>Average CCD decision</small><strong>{formatHours(metrics.averageCcdHours)}</strong></div>
      </div>

      <div className="operations-breakdown-grid">
        <Breakdown title="By request type" entries={sortedEntries(metrics.byType)} label={typeLabel} />
        <Breakdown title="By company" entries={sortedEntries(metrics.byCompany)} />
        <Breakdown title="By workflow status" entries={sortedEntries(metrics.byStatus)} />
      </div>
    </div>

    <AdminSettingsPanel settings={metrics.adminSettings} getAccessToken={getAccessToken} />
  </>;
}
